import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DetailsOverlay,
  type DetailsOverlayProps,
} from "@/components/canvas/detailsSide/utils/DetailsOverlay";
import { Button } from "components/common/Button";
import { SearchBar } from "components/common/SearchBar";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import type { ColumnItem } from "../ColumnListElement";
import { HeuristicBanner } from "./HeuristicBanner";
import { CurrentAssignmentBadge } from "./CurrentAssignmentBadge";
import { CrossKindWarningBar } from "./CrossKindWarningBar";
import { UnitRow } from "./UnitRow";
import { UnitInfoCard } from "./UnitInfoCard";
import { QkindChipStrip, type QkindChip } from "./QkindChipStrip";
import { ScaleChipRow } from "./ScaleChipRow";
import { ScaleExponentInput } from "./ScaleExponentInput";
import { useUnitSearch } from "./useUnitSearch";
import { useCommonUnits } from "./useCommonUnits";
import { useHeuristicMatch } from "./useHeuristicMatch";
import { useUnitDescription } from "./useUnitDescription";
import { useUnitLookup } from "./useUnitLookup";
import { useQkindContext } from "./useQkindContext";
import { formatQkindLabel } from "./formatQkindLabel";
import { deriveScaleLabel } from "./types";
import type { UnitAssignment, UnitDetails } from "./types";

/// Picker width is load-bearing: the info overlay shifts left by exactly this
/// much to sit flush against the picker's left edge.
const PICKER_WIDTH = 280;
const INFO_WIDTH = 320;

interface UnitPickerViewProps {
  tableId: string;
  column: ColumnItem;
  canEdit: boolean;
  onClose: () => void;
  /** Forwarded to every overlay this view can render. The canvas action bar
   *  passes `"above"` + its slot; the details pane leaves both unset. */
  side?: DetailsOverlayProps["side"];
  anchorTo?: HTMLElement | null;
  /** Takes the pick in place of the live table, for a column that is not one
   *  (a design draft's). `null` clears the unit. */
  onApply?: (assignment: UnitAssignment | null) => void;
}

function exponentFromAssignment(a: UnitAssignment | null | undefined): number {
  if (!a) return 0;
  return a.scale_exponent;
}

export function UnitPickerView({
  tableId,
  column,
  canEdit,
  onClose,
  side,
  anchorTo,
  onApply,
}: UnitPickerViewProps) {
  const placement = { side, anchorTo };
  const clientApp = useBoundStore((s) => s.clientApp);
  const columnName = column.name ?? "";
  const current = column.unitAssignment ?? null;

  // ---------- Staged state ----------
  // The picker is transactional: pending picks land on Apply, not on click.
  const [stagedUnit, setStagedUnit] = useState<UnitDetails | null>(null);
  const initialExponent = useMemo(
    () => exponentFromAssignment(current),
    [current],
  );
  const [stagedExponent, setStagedExponent] = useState<number>(initialExponent);
  const [carriedScale, setCarriedScale] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeQkindUri, setActiveQkindUri] = useState<string | null>(null);
  const [heuristicDismissed, setHeuristicDismissed] = useState(false);
  const [stagedClear, setStagedClear] = useState(false);
  const [focusedRowIdx, setFocusedRowIdx] = useState<number | null>(null);
  const [infoUnit, setInfoUnit] = useState<UnitDetails | null>(null);
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useRef(`unit-listbox-${column.columnId}`).current;

  // ---------- Data fetching ----------
  const heuristicUri = useHeuristicMatch(columnName);
  const searchState = useUnitSearch({
    query: searchQuery,
    quantityKindUri: activeQkindUri,
  });
  const commonUnitsState = useCommonUnits(activeQkindUri);
  // Lazy per-URI description cache, prefetched on row hover so the info
  // overlay usually opens with its prose already in hand.
  const descriptions = useUnitDescription();
  // Resolve the current assignment + heuristic match against the FULL catalog
  // (not just the 20-entry curated commons list). The earlier find-in-commons
  // approach falsely flagged any non-curated URI as "no longer in catalog" on
  // picker reopen — `unit:RAD-PER-SEC`, most CCY_*, most everything outside
  // the curated set. One PK round-trip each via `/api/units/lookup`; cached
  // per picker instance inside the hook.
  const currentLookup = useUnitLookup(current?.stored_unit_uri);
  const currentDetails = currentLookup.details;
  // Heuristic match → only fetch once the user hasn't dismissed it. Reusing
  // the same lookup hook gives us the qkind label without a second request
  // when the heuristic URI happens to equal the current assignment.
  const heuristicLookup = useUnitLookup(
    heuristicDismissed ? null : heuristicUri,
  );
  const heuristicDetails = heuristicLookup.details;
  // Ancestor + children chips for the pinned kind. Fetched only when a chip
  // is actually pinned; the hook returns idle/null otherwise.
  const qkindContextState = useQkindContext(activeQkindUri);

  // ---------- Derived: shown unit rows ----------
  const isSearching = searchQuery.trim().length > 0;
  const isBrowsingPinnedKind = activeQkindUri !== null;
  // Search wins over the pin: when both are active, the backend filters the
  // search within the pinned qkind's subtree (descendant walk), so the
  // search result IS the in-pin filtered list. Idle + pinned falls back to
  // the common-units browse for the same subtree.
  const shownUnits: UnitDetails[] = useMemo(() => {
    if (isSearching && searchState.result) {
      // Map search hits to a minimal UnitDetails shape for the row.
      return searchState.result.units.map((u) => ({
        uri: u.uri,
        source: u.source,
        label: u.label,
        symbol: u.symbol,
        alpha_code: u.alpha_code,
        minor_units: null,
        quantity_kind_uri: u.quantity_kind_uri,
        quantity_kind_label: u.quantity_kind_label,
        multiplier_to_si: null,
        offset_to_si: null,
        ucum_code: null,
        unece_code: null,
        dimension_vector: null,
        deprecated: u.deprecated,
        // Search-hit rows don't carry exact_matches over the wire — the
        // info card hydrates the full UnitDetails via /api/units/lookup
        // when opened, which has the populated field. Empty here is correct.
        exact_matches: [],
      }));
    }
    return commonUnitsState.units;
  }, [isSearching, searchState.result, commonUnitsState.units]);

  // ---------- Derived: chip strip contents ----------
  // Three modes, in priority order:
  //   1. A kind is pinned → render ancestors + pinned + children from the
  //      qkind_context endpoint. Users navigate up the hierarchy via the
  //      ancestor chips and drill down via child chips. Same flow whether
  //      or not a search is active (search filters the unit list within the
  //      pinned subtree, the chip strip stays as navigation).
  //   2. Typing with no pin → qkinds matching the query from search results.
  //   3. Idle, no pin → heuristic / current-assignment kind plus neighbors
  //      from the visible unit rows (legacy behavior).
  const chips: QkindChip[] = useMemo(() => {
    const out: QkindChip[] = [];
    const seen = new Set<string>();
    const push = (
      uri: string | null | undefined,
      label: string | null | undefined,
    ) => {
      if (!uri || !label) return;
      if (seen.has(uri)) return;
      seen.add(uri);
      out.push({ uri, label });
    };

    if (activeQkindUri && qkindContextState.result) {
      // Ancestors render LEFT of the pinned chip (root farthest, direct
      // parent nearest). The pinned chip is added by the QkindChipStrip's
      // reorder logic — we still need to push it so the strip can find it.
      const ctx = qkindContextState.result;
      // Reverse so root is first → moves toward pinned as the user reads
      // left to right.
      for (let i = ctx.ancestors.length - 1; i >= 0; i--) {
        push(ctx.ancestors[i].uri, ctx.ancestors[i].label);
      }
      // Pinned kind. Label fallback chain: context (won't have it; ctx
      // doesn't include self) → current/heuristic details → URI suffix.
      const pinnedLabel =
        currentDetails?.quantity_kind_uri === activeQkindUri
          ? (currentDetails?.quantity_kind_label ?? null)
          : heuristicDetails?.quantity_kind_uri === activeQkindUri
            ? (heuristicDetails?.quantity_kind_label ?? null)
            : (shownUnits.find((u) => u.quantity_kind_uri === activeQkindUri)
                ?.quantity_kind_label ?? null);
      push(
        activeQkindUri,
        pinnedLabel ?? activeQkindUri.replace(/^quantitykind:/, ""),
      );
      // Children render RIGHT of the pinned chip for drill-down.
      for (const c of ctx.children) push(c.uri, c.label);
      return out;
    }

    if (isSearching && searchState.result) {
      for (const q of searchState.result.qkinds) push(q.uri, q.label);
      // Pinned-but-context-still-loading fallback: keep the pin visible even
      // before qkindContextState resolves so the chip strip doesn't flicker.
      if (activeQkindUri && !seen.has(activeQkindUri)) {
        push(activeQkindUri, activeQkindUri.replace(/^quantitykind:/, ""));
      }
      return out;
    }

    // Idle, nothing pinned, nothing typed.
    if (heuristicDetails) {
      push(
        heuristicDetails.quantity_kind_uri,
        heuristicDetails.quantity_kind_label,
      );
    } else if (currentDetails) {
      push(
        currentDetails.quantity_kind_uri,
        currentDetails.quantity_kind_label,
      );
    }
    for (const u of shownUnits.slice(0, 12)) {
      if (out.length >= 6) break;
      push(u.quantity_kind_uri, u.quantity_kind_label);
    }
    return out;
  }, [
    isSearching,
    searchState.result,
    heuristicDetails,
    currentDetails,
    shownUnits,
    activeQkindUri,
    qkindContextState.result,
  ]);

  // ---------- Apply gate ----------
  const stagedAssignment: UnitAssignment | null = useMemo(() => {
    if (stagedClear) return null;
    if (!stagedUnit) return null;
    // Carry the display fields the picker already resolved through the
    // optimistic local apply so the column-row badge appears the instant the
    // user hits "Apply" — without waiting for the server's broadcast to
    // round-trip back. The server still decorates the broadcast (defense in
    // depth) and `PartialEq` ignores these fields so no spurious version
    // bumps. Currency entries surface their alpha_code here even when symbol
    // is null.
    return {
      stored_unit_uri: stagedUnit.uri,
      stored_unit_source: stagedUnit.source,
      scale_exponent: stagedExponent,
      symbol: stagedUnit.symbol,
      label: stagedUnit.label,
      alpha_code: stagedUnit.alpha_code,
    };
  }, [stagedUnit, stagedExponent, stagedClear]);

  const isCrossKind = useMemo(() => {
    if (!current || !stagedUnit) return false;
    if (!currentDetails?.quantity_kind_uri || !stagedUnit.quantity_kind_uri)
      return false;
    return currentDetails.quantity_kind_uri !== stagedUnit.quantity_kind_uri;
  }, [current, stagedUnit, currentDetails?.quantity_kind_uri]);

  const hasChange = useMemo(() => {
    if (stagedClear) return current !== null;
    if (!stagedUnit) return false;
    if (!current) return true;
    if (!stagedAssignment) return false;
    return (
      current.stored_unit_uri !== stagedAssignment.stored_unit_uri ||
      current.stored_unit_source !== stagedAssignment.stored_unit_source ||
      current.scale_exponent !== stagedAssignment.scale_exponent
    );
  }, [stagedClear, stagedAssignment, current, stagedUnit]);

  const applyDisabled = !canEdit || !hasChange;

  /** Render an exponent as its display label: a curated short label when one
   *  exists (`K`/`M`/`%`/…), `10^N` otherwise, empty string for `0`. */
  const exponentLabel = (exp: number): string => {
    const short = deriveScaleLabel(exp);
    if (short !== null) return short;
    if (exp === 0) return "";
    return `10^${exp}`;
  };

  // ---------- Footer summary string ----------
  const footerSummary = useMemo<string>(() => {
    if (stagedClear) return "Clear unit assignment";
    if (!stagedUnit) {
      if (!current) return "No selection yet";
      const label = currentDetails?.label ?? current.stored_unit_uri;
      const s = exponentLabel(current.scale_exponent);
      return `${label}${s ? ` · ${s}` : ""}  (no change)`;
    }
    const label = stagedUnit.label ?? stagedUnit.uri;
    if (
      isCrossKind &&
      currentDetails?.quantity_kind_label &&
      stagedUnit.quantity_kind_label
    ) {
      return `${formatQkindLabel(currentDetails.quantity_kind_label)} → ${formatQkindLabel(stagedUnit.quantity_kind_label)}`;
    }
    const s = exponentLabel(stagedExponent);
    return `${label}${s ? ` · ${s}` : ""}`;
  }, [
    stagedClear,
    stagedUnit,
    current,
    currentDetails?.label,
    currentDetails?.quantity_kind_label,
    isCrossKind,
    stagedExponent,
  ]);

  const applyButtonLabel = stagedClear
    ? "Clear"
    : isCrossKind
      ? "Confirm"
      : "Apply";

  // ---------- Behaviors ----------
  const handlePickUnit = useCallback(
    (u: UnitDetails) => {
      // Clicking the already-staged unit toggles back to the picker's
      // initial state (a no-op Apply), letting the user undo a tentative
      // pick without using Cancel.
      if (stagedUnit?.uri === u.uri) {
        setStagedUnit(null);
        setStagedClear(false);
        setStagedExponent(initialExponent);
        setCarriedScale(false);
        return;
      }
      setStagedClear(false);
      // Scale carry-over within same qkind (per design plan §"Scale persistence").
      const prevUnit = stagedUnit ?? null;
      const carryQkind =
        prevUnit?.quantity_kind_uri ??
        currentDetails?.quantity_kind_uri ??
        null;
      const sameKind = carryQkind && carryQkind === u.quantity_kind_uri;
      setStagedUnit(u);
      if (sameKind) {
        // Keep current exponent; surface "(kept)" indicator unless we're at 0.
        setCarriedScale(stagedExponent !== 0);
      } else {
        setStagedExponent(0);
        setCarriedScale(false);
      }
    },
    [
      stagedUnit,
      currentDetails?.quantity_kind_uri,
      stagedExponent,
      initialExponent,
    ],
  );

  const handlePickExponent = useCallback((exponent: number) => {
    setStagedExponent(exponent);
    setCarriedScale(false);
  }, []);

  const handleApply = useCallback(async () => {
    if (!canEdit || applyDisabled) return;
    if (onApply) {
      onApply(stagedClear ? null : stagedAssignment);
      onClose();
      return;
    }
    if (!clientApp) return;
    try {
      if (stagedClear) {
        clientApp.set_column_unit_assignment(tableId, column.columnId, null);
      } else if (stagedAssignment) {
        clientApp.set_column_unit_assignment(
          tableId,
          column.columnId,
          stagedAssignment,
        );
      }
      onClose();
    } catch (err) {
      // Per eng decision 4b: toast + close picker on WASM throw.
      const msg =
        err instanceof Error
          ? err.message
          : "Could not save — column may have been changed";
      showNotificationGlobal(msg, "error");
      onClose();
    }
  }, [
    clientApp,
    canEdit,
    applyDisabled,
    stagedClear,
    stagedAssignment,
    tableId,
    column.columnId,
    onClose,
    onApply,
  ]);

  const handleToggleQkind = useCallback((uri: string) => {
    setActiveQkindUri((cur) => {
      const next = cur === uri ? null : uri;
      // S1.1: when transitioning from "search active, no pin" to "pinned",
      // clear the search text. The chip becomes the navigation gesture, not
      // an AND filter on top of free-text. Unpinning leaves the query alone
      // — the user might want to keep typing in a different context.
      if (cur === null && next !== null) {
        setSearchQuery("");
      }
      return next;
    });
    // Clearing or changing the qkind filter doesn't change the staged unit
    // unless that unit's kind no longer matches — we let the result list
    // re-render and the user re-pick if needed.
  }, []);

  const handleStageClear = useCallback(() => {
    setStagedClear(true);
    setStagedUnit(null);
  }, []);

  const handlePickReplacement = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  // ---------- Keyboard ----------
  const listRef = useRef<HTMLDivElement>(null);
  // Only the keys scroll: on hover the list would jump under the pointer.
  const focusRow = useCallback((idx: number) => {
    setFocusedRowIdx(idx);
    const rows = listRef.current?.querySelectorAll('[role="option"]');
    rows?.[idx]?.scrollIntoView?.({ block: "nearest" });
  }, []);
  const handleListKey = useCallback(
    (e: KeyboardEvent | React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (shownUnits.length > 0)
          focusRow(
            focusedRowIdx === null
              ? 0
              : Math.min(focusedRowIdx + 1, shownUnits.length - 1),
          );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (focusedRowIdx === null || focusedRowIdx === 0) {
          searchInputRef.current?.focus();
          setFocusedRowIdx(null);
        } else focusRow(focusedRowIdx - 1);
      } else if (e.key === "Enter") {
        if (focusedRowIdx !== null && shownUnits[focusedRowIdx]) {
          e.preventDefault();
          handlePickUnit(shownUnits[focusedRowIdx]);
        }
      }
    },
    [shownUnits, focusedRowIdx, handlePickUnit, focusRow],
  );

  // Drawer-wide Cmd+Enter applies; Esc 2-stage clears search then closes.
  const handleDrawerKey = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!applyDisabled) handleApply();
      } else if (e.key === "Escape") {
        // Esc peels one layer at a time: info overlay, then search, then close.
        if (infoUnit) {
          e.preventDefault();
          setInfoUnit(null);
        } else if (searchQuery) {
          e.preventDefault();
          setSearchQuery("");
        } else {
          e.preventDefault();
          onClose();
        }
      }
    },
    [applyDisabled, handleApply, searchQuery, onClose, infoUnit],
  );

  // Autofocus search on mount; reset focused row when list changes.
  useEffect(() => {
    if (canEdit) searchInputRef.current?.focus();
  }, [canEdit]);

  useEffect(() => {
    setFocusedRowIdx(null);
    // The info overlay anchors to a row element. Once the list changes that row
    // may be unmounted, and the overlay would freeze at its last position while
    // describing a unit no longer listed.
    setInfoUnit(null);
    setInfoAnchor(null);
  }, [shownUnits.length, isSearching]);

  // Hide heuristic banner while typing.
  const showHeuristic = !isSearching && heuristicDetails !== null && !current;

  // Section label for the unit list. Pinned-kind browse wins over text-search
  // because the kind chip is the more specific user gesture.
  const sectionLabel = useMemo<string>(() => {
    if (isBrowsingPinnedKind) {
      const qkindLabel = formatQkindLabel(shownUnits[0]?.quantity_kind_label);
      const base = qkindLabel ? `${qkindLabel.toUpperCase()} UNITS` : "UNITS";
      if (commonUnitsState.loading) return `${base} · loading…`;
      return `${base} · ${shownUnits.length} of catalog`;
    }
    if (isSearching) {
      const count = searchState.result?.units.length ?? 0;
      const status =
        searchState.status === "loading" ? "searching…" : `${count} of catalog`;
      return `UNITS · ${status}`;
    }
    return "COMMON UNITS";
  }, [
    isBrowsingPinnedKind,
    isSearching,
    searchState.status,
    searchState.result?.units.length,
    commonUnitsState.loading,
    shownUnits,
  ]);

  // ---------- Read-only path ----------
  if (!canEdit) {
    return (
      <DetailsOverlay title="Unit" onClose={onClose} {...placement}>
        <div
          style={{
            padding: "var(--spacing-md)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-md)",
          }}
        >
          {current ? (
            <>
              <CurrentAssignmentBadge
                assignment={current}
                details={currentDetails}
                isMissing={currentLookup.notFound}
                canClear={false}
                isStagedForClear={false}
                onStageClear={() => {}}
              />
              <div
                style={{
                  background: "var(--color-bg-hover)",
                  border:
                    "var(--size-border-hairline) solid var(--color-border)",
                  borderRadius: "var(--radius-app)",
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  fontSize: "var(--font-size-small)",
                  color: "var(--color-fg-muted)",
                  lineHeight: 1.4,
                }}
              >
                <strong style={{ fontWeight: 500, color: "var(--color-fg)" }}>
                  Read-only.
                </strong>{" "}
                This column is imported. The owner sets the unit.
              </div>
            </>
          ) : (
            <div
              style={{
                padding: "var(--spacing-md)",
                textAlign: "center",
                color: "var(--color-fg-muted)",
                fontSize: "var(--font-size-small)",
              }}
            >
              No unit assigned.
            </div>
          )}
        </div>
      </DetailsOverlay>
    );
  }

  // ---------- Edit path ----------
  // Layout: the modal holds one height, the cap or the room it has, so typing
  // never resizes it. The listbox is the only scrolling region — it owns
  // `overflow-y: auto` and shrinks via flex, down to two rows, to absorb any
  // overflow, so unit rows can never visually escape and collide with the
  // SCALE chips or footer below. Above-list bits (badge, search, kind chips,
  // list label) and below-list bits (SCALE chips, footer) are pinned with
  // `flex-shrink: 0`; past that the whole body scrolls under a sticky footer.
  // The SCALE section disappears entirely when a clear is staged — it's
  // irrelevant in that state.
  const labelStyle: React.CSSProperties = {
    fontSize: "var(--font-size-xx-small)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--color-fg-muted)",
    fontWeight: 500,
  };

  const infoDesc = infoUnit ? descriptions.get(infoUnit.uri) : undefined;

  const inline = side === "inline";
  const info = infoUnit && (
    <DetailsOverlay
      title={infoUnit.label ?? infoUnit.uri}
      floatingClose
      onClose={() => setInfoUnit(null)}
      width={INFO_WIDTH}
      // Stack flush against the picker's left edge, top-aligned to the row.
      offsetFromPanel={PICKER_WIDTH}
      anchorTo={inline ? undefined : infoAnchor}
      side={inline ? "inline" : undefined}
      focusKey={infoUnit.uri}
    >
      <div className="overflow-y-auto scrollbar-overlay-hover">
        <UnitInfoCard
          unit={infoUnit}
          description={infoDesc?.state === "loaded" ? infoDesc.text : null}
          descriptionLoading={infoDesc?.state === "loading"}
        />
      </div>
    </DetailsOverlay>
  );
  const picker = (
    <DetailsOverlay
      title="Set unit"
      onClose={onClose}
      width={PICKER_WIDTH}
      maxHeight="var(--size-unit-picker-max)"
      {...placement}
    >
      {/* Scrolls only as a last resort, when even the pinned sections do
            not fit; the footer stays in view. */}
      <div
        onKeyDown={handleDrawerKey}
        style={{
          display: "flex",
          flexDirection: "column",
          height: inline ? "100%" : "var(--size-unit-picker-max)",
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {/* Pinned-top: above-list controls */}
        <div
          style={{
            flexShrink: 0,
            padding: "var(--spacing-md)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-md)",
          }}
        >
          {showHeuristic && heuristicDetails && (
            <HeuristicBanner
              details={heuristicDetails}
              onApply={() => {
                handlePickUnit(heuristicDetails);
                // Apply on next tick when the staged state is fresh.
                setTimeout(handleApply, 0);
              }}
              onDismiss={() => setHeuristicDismissed(true)}
            />
          )}

          {current && (
            <CurrentAssignmentBadge
              assignment={current}
              details={currentDetails}
              isMissing={currentLookup.notFound}
              canClear={true}
              isStagedForClear={stagedClear}
              onStageClear={handleStageClear}
              onPickReplacement={handlePickReplacement}
            />
          )}

          {isCrossKind &&
            currentDetails?.quantity_kind_label &&
            stagedUnit?.quantity_kind_label && (
              <CrossKindWarningBar
                fromKindLabel={formatQkindLabel(
                  currentDetails.quantity_kind_label,
                )}
                toKindLabel={formatQkindLabel(stagedUnit.quantity_kind_label)}
              />
            )}

          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search units or kinds"
            leadingIcon="search"
            isLoading={searchState.status === "loading"}
            searchBarRef={
              searchInputRef as unknown as React.MutableRefObject<HTMLDivElement | null>
            }
            error={searchState.status === "error" ? searchState.error : null}
            errorDisplay="text"
          />

          {chips.length > 0 && (
            <div style={labelStyle} aria-hidden>
              QUANTITY
            </div>
          )}
          <QkindChipStrip
            chips={chips}
            activeUri={activeQkindUri}
            onToggle={handleToggleQkind}
          />

          <div style={labelStyle} aria-hidden>
            {sectionLabel}
          </div>
        </div>

        {/* Scrolling listbox: grows to absorb leftover vertical space so the
            SCALE section + footer stay pinned to the bottom of the modal
            regardless of result count. With `flex: 0 1 auto` (the prior
            value), a short list (e.g. 9 search hits) left a visible gap
            between the rows and the SCALE row — the modal kept its tall
            shape but the rows were content-sized, so the slack landed in
            between sections. `flex: 1 1 auto` puts the slack inside the
            listbox below the rows (invisible — no rows there) and shrinks
            cleanly on overflow. Do NOT revert. */}
        <div
          ref={listRef}
          role="listbox"
          id={listboxId}
          aria-label={sectionLabel}
          tabIndex={0}
          onKeyDown={handleListKey}
          style={{
            flex: "1 1 auto",
            minHeight: "calc(2 * var(--control-height-md))",
            overflowY: "auto",
            overscrollBehavior: "contain",
            display: "flex",
            flexDirection: "column",
            padding: "0 var(--spacing-md) var(--spacing-md)",
          }}
        >
          {shownUnits.length === 0 &&
            (isBrowsingPinnedKind || !isSearching) &&
            commonUnitsState.loading && (
              <div
                style={{
                  padding: "var(--spacing-md)",
                  textAlign: "center",
                  color: "var(--color-fg-muted)",
                  fontSize: "var(--font-size-small)",
                }}
              >
                Loading…
              </div>
            )}
          {shownUnits.length === 0 &&
            !isBrowsingPinnedKind &&
            isSearching &&
            searchState.status !== "loading" &&
            searchState.status !== "error" && (
              <div
                style={{
                  padding: "var(--spacing-md)",
                  textAlign: "center",
                  color: "var(--color-fg-muted)",
                  fontSize: "var(--font-size-small)",
                  border:
                    "var(--size-border-hairline) dashed var(--color-border)",
                  borderRadius: "var(--radius-app)",
                }}
              >
                <div style={{ color: "var(--color-fg)", fontWeight: 500 }}>
                  No units match &ldquo;{searchQuery.trim()}&rdquo;
                </div>
                <div style={{ marginTop: "var(--spacing-xs)" }}>
                  Check spelling, search by symbol, or pick a kind above to
                  browse.
                </div>
              </div>
            )}
          {shownUnits.map((u, idx) => {
            return (
              <UnitRow
                key={u.uri}
                unit={u}
                selected={stagedUnit?.uri === u.uri}
                focused={focusedRowIdx === idx}
                id={`unit-row-${u.uri}`}
                onSelect={() => handlePickUnit(u)}
                onMouseEnter={() => {
                  setFocusedRowIdx(idx);
                  // Idempotent — only hits the network on first hover per URI.
                  // Prefetching on hover means the info overlay usually opens
                  // with its prose already in hand.
                  descriptions.fetch(u.uri);
                }}
                onOpenInfo={(anchor) => {
                  setInfoUnit(u);
                  setInfoAnchor(anchor);
                  descriptions.fetch(u.uri);
                }}
                infoOpen={infoUnit?.uri === u.uri}
              />
            );
          })}
        </div>

        {/* Pinned scale section. Hidden during a staged clear — scale is
            meaningless when clearing. */}
        {!stagedClear && (
          <div
            style={{
              flexShrink: 0,
              borderTop:
                "var(--size-border-hairline) solid var(--color-border)",
              padding: "var(--spacing-md)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-sm)",
            }}
          >
            <div style={labelStyle} aria-hidden>
              SCALE
            </div>
            <ScaleChipRow
              active={stagedExponent}
              isCarried={carriedScale}
              onPick={handlePickExponent}
            />
            <ScaleExponentInput
              value={stagedExponent}
              onChange={handlePickExponent}
            />
          </div>
        )}

        {/* Visually-hidden live region for screen readers. */}
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {isSearching && searchState.result
            ? `${searchState.result.units.length} units match ${searchQuery.trim()}`
            : ""}
        </div>

        {/* Footer */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            flexShrink: 0,
            borderTop: "var(--size-border-hairline) solid var(--color-border)",
            padding: "var(--spacing-sm) var(--spacing-md)",
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-xs)",
            background: "var(--color-bg)",
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: "var(--font-size-small)",
              color: "var(--color-fg-muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {footerSummary}
          </span>
          <Button variant="default" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={stagedClear ? "destructive" : "primary"}
            size="sm"
            onClick={handleApply}
            disabled={applyDisabled}
          >
            {applyButtonLabel}
          </Button>
        </div>
      </div>
    </DetailsOverlay>
  );
  // In place, a unit's details stack above the picker instead of beside it.
  return inline ? (
    <div className="flex flex-col gap-2 h-full min-h-0">
      {info && (
        <div className="shrink-0 max-h-[50%] overflow-y-auto">{info}</div>
      )}
      <div className="flex-1 min-h-0">{picker}</div>
    </div>
  ) : (
    <>
      {info}
      {picker}
    </>
  );
}
